import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { VOCABULARY } from '@vello/country-vocabularies';
import { translate, translatePlural } from '@vello/i18n';
import { type AdminLang, CATALOGS, DEFAULT_LANG } from './messages';

const STORAGE_KEY = 'vello.admin.lang';

type Vars = Readonly<Record<string, string | number>>;

export interface I18nValue {
  readonly lang: AdminLang;
  readonly setLang: (lang: AdminLang) => void;
  /** Resolve a message id (fail-soft: returns the id if missing, never throws). */
  readonly t: (id: string, vars?: Vars) => string;
  /** Resolve a pluralized message (`{baseId}.one` / `{baseId}.other`), CLDR-selected by the active language. */
  readonly tp: (baseId: string, count: number, vars?: Vars) => string;
}

const I18nContext = createContext<I18nValue | undefined>(undefined);

function isLang(v: unknown): v is AdminLang {
  return v === 'pt' || v === 'en' || v === 'es' || v === 'it';
}

/**
 * Detect a supported language from the browser's preference list, in order. Matches by the primary subtag only
 * (e.g. any Portuguese regional variant → `pt`, any English → `en`, Spanish → `es`, Italian → `it`), so a
 * region-specific browser/OS locale opens in the right language without hardcoding a region tag. Returns
 * undefined when none of the base languages is preferred.
 */
function detectLang(): AdminLang | undefined {
  try {
    const prefs = Array.isArray(navigator.languages) && navigator.languages.length > 0 ? navigator.languages : [navigator.language];
    for (const pref of prefs) {
      if (typeof pref !== 'string') continue;
      const primary = pref.toLowerCase().split('-')[0];
      if (primary === 'pt') return 'pt';
      if (primary === 'en') return 'en';
      if (primary === 'es') return 'es';
      if (primary === 'it') return 'it';
    }
  } catch {
    // navigator unavailable (non-browser/SSR) → no detection
  }
  return undefined;
}

/**
 * Resolve the initial language, most-specific first: the operator's persisted choice wins; otherwise the
 * browser's detected language; otherwise the console fallback (`DEFAULT_LANG`). Fail-soft — any storage error
 * skips to detection.
 */
function loadLang(): AdminLang {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (isLang(raw)) return raw;
  } catch {
    // private mode / storage disabled → fall through to detection
  }
  return detectLang() ?? DEFAULT_LANG;
}

/**
 * An error sentence carries the server's code in `{code}` ("Could not load ({code})"). A raw code such as `NOT_FOUND` is
 * for a developer, not an operator: when `code` is one of the API's public codes, the central catalog's `api.error_code`
 * sentence stands in for it (D577 — one source, no second copy in the console); any other value (a ride code) is left as it is.
 */
function plainCodes(lang: AdminLang, vars: Vars | undefined): Vars | undefined {
  const code = vars?.['code'];
  if (typeof code !== 'string' || !VOCABULARY.has('api.error_code', code)) return vars;
  return { ...vars, code: VOCABULARY.label('api.error_code', code, lang).replace(/[.\s]+$/, '') };
}

export function I18nProvider({ children }: { children: ReactNode }): ReactElement {
  const [lang, setLangState] = useState<AdminLang>(() => loadLang());

  useEffect(() => {
    document.documentElement.lang = lang;
    // D856 (owner D845 Q5): the tab title is the console's one name, the same in every language.
    document.title = translate(CATALOGS[lang], 'chrome.brand').text;
  }, [lang]);

  const setLang = useCallback((next: AdminLang): void => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // persistence is best-effort; the choice still applies for this session
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const catalog = CATALOGS[lang];
    return {
      lang,
      setLang,
      t: (id, vars) => translate(catalog, id, plainCodes(lang, vars)).text,
      tp: (baseId, count, vars) => translatePlural(catalog, baseId, count, vars).text,
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): I18nValue {
  const value = useContext(I18nContext);
  if (value === undefined) throw new Error('useT must be used within an I18nProvider');
  return value;
}
